import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Lock, ArrowRight, Check, X, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Validation du mot de passe - règles individuelles
  const passwordRules = {
    minLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password)
  };

  // Vérifier si les mots de passe correspondent
  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;

  // Vérifier si le token est présent
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
    } else {
      // Pour l'instant, on considère le token comme valide s'il est présent
      // Dans une vraie implémentation, on vérifierait côté serveur
      setTokenValid(true);
    }
  }, [token]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('au moins 8 caractères');
    if (!/[A-Z]/.test(password)) errors.push('une majuscule');
    if (!/[a-z]/.test(password)) errors.push('une minuscule');
    if (!/[0-9]/.test(password)) errors.push('un chiffre');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation mot de passe
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      toast.error(`Le mot de passe doit contenir : ${passwordErrors.join(', ')}`);
      return;
    }

    // Confirmation mot de passe
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: formData.password
      });
      
      toast.success('Mot de passe réinitialisé avec succès !');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la réinitialisation';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Composant pour afficher une règle de validation
  const PasswordRule = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-xs ${valid ? 'text-emerald-600' : 'text-slate-400'}`}>
      {valid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{text}</span>
    </div>
  );

  // Si pas de token ou token invalide
  if (tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="font-chivo text-2xl">Lien invalide</CardTitle>
            <CardDescription className="text-slate-500">
              Ce lien de réinitialisation est invalide ou a expiré.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Retour à la connexion
            </Button>
            <p className="text-sm text-slate-500 text-center">
              Besoin d'un nouveau lien ?{' '}
              <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700 font-medium">
                Demander un nouveau lien
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Chargement de la validation du token
  if (tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="p-2 bg-blue-600 rounded-lg">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <span className="font-chivo font-bold text-2xl text-slate-900 tracking-tight">
              5minCompta
            </span>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-6">
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <ShieldCheck className="h-6 w-6 text-blue-600" />
              </div>
              <CardTitle className="font-chivo text-2xl text-center">
                Nouveau mot de passe
              </CardTitle>
              <CardDescription className="text-slate-500 text-center">
                Créez un nouveau mot de passe sécurisé pour votre compte
              </CardDescription>
            </CardHeader>
            
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Nouveau mot de passe *
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                  </div>
                  {/* Règles de validation du mot de passe */}
                  {formData.password && (
                    <div className="grid grid-cols-2 gap-1 pt-2">
                      <PasswordRule valid={passwordRules.minLength} text="8 caractères minimum" />
                      <PasswordRule valid={passwordRules.hasUppercase} text="1 majuscule" />
                      <PasswordRule valid={passwordRules.hasLowercase} text="1 minuscule" />
                      <PasswordRule valid={passwordRules.hasNumber} text="1 chiffre" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                    Confirmer le nouveau mot de passe *
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      className={`pl-10 ${formData.confirmPassword && (passwordsMatch ? 'border-emerald-500 focus-visible:ring-emerald-500' : 'border-red-500 focus-visible:ring-red-500')}`}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      data-testid="reset-confirm-password-input"
                    />
                    {formData.confirmPassword && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {passwordsMatch ? (
                          <Check className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <X className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {formData.confirmPassword && !passwordsMatch && (
                    <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                  )}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-4 pt-2">
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-11"
                  disabled={loading || !passwordsMatch || Object.values(passwordRules).some(v => !v)}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Réinitialisation...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Réinitialiser mon mot de passe
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>

                <p className="text-sm text-slate-500 text-center">
                  <Link 
                    to="/login" 
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Retour à la connexion
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1758520145175-aa3b593b81af?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      >
        <div className="h-full w-full bg-blue-900/40 backdrop-blur-[2px] flex items-end p-12">
          <div className="text-white max-w-md">
            <h2 className="font-chivo text-3xl font-bold mb-4">
              Sécurisez votre compte
            </h2>
            <p className="text-white/80 text-lg">
              Choisissez un mot de passe fort pour protéger vos données comptables.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
